import React, { useState } from 'react';
import { Table } from '../../Component/Table';
import { useTranslation } from 'react-i18next';
import { Button, Space, Popconfirm, message, Tag } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Api from '../../Network';
import PermissionModal from './PermissionModal';
import Can from '../../Component/Can';
import NameByLang from '../../Component/nameByLang';
import { AnimatedContainer } from '../../Component/ui';
import './style.scss';

const Permissions = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedPermission, setSelectedPermission] = useState<any>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');

  // Delete permission mutation
  const deletePermissionMutation = useMutation({
    mutationFn: (permissionId: number) => Api.delete(`/api/admin/permissions/${permissionId}`),
    onSuccess: () => {
      message.success(t('permissions.deleteSuccess', 'Permission deleted successfully'));
      queryClient.invalidateQueries({ queryKey: ['/api/admin/permissions'] });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.msg || t('common.error', 'Error deleting permission');
      message.error(errorMessage);
    },
  });

  const handleDelete = (permissionId: number) => {
    deletePermissionMutation.mutate(permissionId);
  };

  const handleEdit = (record: any) => {
    setSelectedPermission(record);
    setModalMode('edit');
    setModalVisible(true);
  };

  const handleAddNew = () => {
    setSelectedPermission(null);
    setModalMode('create');
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setSelectedPermission(null);
  };

  const handleModalSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['/api/admin/permissions'] });
  };

  return (
    <AnimatedContainer className="permissions-page" variant="fadeUp">
      <Table
        tableQueryKey="/api/admin/permissions"
        tableTitle={t('permissions.title', 'Permissions Management')}
        showSearch={true}
        columns={[
          {
            title: t('permissions.columns.displayName', 'Display Name'),
            dataIndex: 'display_name',
            key: 'display_name',
            render: (_: string, record: any) => (
              <span className="bold primary">
                <NameByLang name_en={record.display_name_en} name_ar={record.display_name_ar} />
              </span>
            ),
          },
          {
            title: t('permissions.columns.description', 'Description'),
            dataIndex: 'description',
            key: 'description',
            render: (_: string, record: any) => (
              <NameByLang name_en={record.description_en} name_ar={record.description_ar} />
            ),
          },
          {
            title: t('permissions.columns.model', 'Model'),
            dataIndex: 'menu_models',
            key: 'model',
            render: (_: any, record: any) => {
              const model = record.menu_models?.[0] || record.menuModels?.[0];
              if (!model) return <Tag color="default">-</Tag>;
              return (
                <Tag color="blue">
                  <NameByLang
                    name_en={model.name_en}
                    name_ar={model.name_ar}
                    name={model.name || model.key}
                  />
                </Tag>
              );
            },
          },
          {
            title: t('permissions.columns.category', 'Column Position'),
            dataIndex: 'category',
            key: 'category',
            render: (category: string) => {
              if (!category) return <Tag color="default">-</Tag>;
              const categoryLabels: Record<string, string> = {
                view_all: t('permissions.columns.viewAll', 'View All'),
                view_details: t('permissions.columns.viewDetails', 'View Details'),
                create: t('permissions.columns.create', 'Create'),
                update: t('permissions.columns.update', 'Update'),
                delete: t('permissions.columns.delete', 'Delete'),
                other: t('permissions.columns.other', 'Other'),
              };
              return <Tag color="green">{categoryLabels[category] || category}</Tag>;
            },
          },
          {
            title: t('common.actions', 'Actions'),
            key: 'actions',
            render: (_: any, record: any) => (
              <Space>
                <Can permission="permissions.update">
                  <Button
                    type="link"
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(record)}
                    size="small"
                  >
                    {t('common.edit', 'Edit')}
                  </Button>
                </Can>
                <Can permission="permissions.delete">
                  <Popconfirm
                    title={t(
                      'permissions.deleteConfirm',
                      'Are you sure you want to delete this permission?'
                    )}
                    description={t('permissions.deleteWarning', 'This action cannot be undone.')}
                    onConfirm={() => handleDelete(record.id)}
                    okText={t('common.yes', 'Yes')}
                    cancelText={t('common.no', 'No')}
                  >
                    <Button type="link" danger icon={<DeleteOutlined />} size="small">
                      {t('common.delete', 'Delete')}
                    </Button>
                  </Popconfirm>
                </Can>
              </Space>
            ),
          },
        ]}
        buttonContent={t('permissions.addNew', 'Add New Permission')}
        onClickNewButton={handleAddNew}
        createPermission="permissions.create"
      />

      <PermissionModal
        visible={modalVisible}
        mode={modalMode}
        permission={selectedPermission}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
      />
    </AnimatedContainer>
  );
};

export default Permissions;
